import { AfterViewInit, booleanAttribute, Directive, ElementRef, EventEmitter, input, Input, OnInit, Optional, Output, Renderer2, ViewContainerRef } from '@angular/core';
import { AbstractControl, FormControl, NgControl } from '@angular/forms';
import { delay, Subject } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { takeUntil } from 'rxjs/operators';
import { MatFormField } from '@angular/material/form-field';
import '../../3rd-party-libraries/module-augmentation';

/** 🧹 **RsAddClearFormFieldButtonDirective**
 *
 * Appends a **clear button** to a `mat-form-field`, providing a mechanism to **reset the form field's value**.
 * The button's visibility is automatically managed: it appears when the field has a value and is not disabled/readonly.
 *
 * **Usage: **
 *
 * ```html
 * <mat-form-field
 *  rsAddClearFormFieldButton
 *  [rsClearFormFieldButtonCallback]='Function.bind(this)'
 *  [rsClearFormFieldButtonDisabled]='boolean'
 *  [rsClearFormFieldButtonResetValue]='unknown'
 *  rsClearFormFieldButtonFocusAndSelectValueAfterReset
 *  (onRsClearFormFieldButtonClicked)='Function()'
 * >
 * ...
 * </mat-form-field>
 * ```
 */
@Directive({
  selector: '[rsAddClearFormFieldButton]',
})
export class RsAddClearFormFieldButtonDirective implements OnInit, AfterViewInit {
  /** Overrides default click events (reset value) with provided custom callback
   *
   * Usage:
   *
   * ```html
   <mat-form-field [rsClearFormFieldButtonCallback]='Function.bind(this) > Function to execute after click event'>
   ...
   </mat-form-field>
   ```
   */
  @Input('rsClearFormFieldButtonCallback') public rsClearFormFieldButtonCallback?: Function;

  /**
   * @input rsClearFormFieldButtonResetValue
   * @description
   * The value to which the bound `FormControl` should be reset when the clear button is clicked.
   *
   * **Behavior:**
   * - If a value (e.g., `0`, `null`, `''`) is explicitly provided via this input, the form control
   * is reset to that specific value using `FormControl.reset(providedValue)`.
   * - If this input is **omitted** from the template, the form control is reset to its
   * **original initial value** using `FormControl.reset()`, relying on Angular's default behavior.
   * - This input is ignored if a custom `rsClearFormFieldButtonCallback` is provided.
   */
  public readonly rsClearFormFieldButtonResetValue = input<unknown>(undefined);

  /**
   * @input rsClearFormFieldButtonFocusAndSelectValueAfterReset
   * @description
   * When this attribute is present on the `mat-form-field`, the directive will attempt to **focus**
   * the underlying `<input>` or `<textarea>` element and **select its content** immediately after the
   * form control has been reset.
   *
   * This is useful for user experience, allowing the user to quickly begin typing a new value.
   * * **Note:** This input is a **boolean attribute**, meaning its mere presence in the template sets the value to `true`.
   * * ```html
   * * <mat-form-field rsClearFormFieldButtonFocusAndSelectValueAfterReset>...</mat-form-field>
   * ```
   */
  public readonly rsClearFormFieldButtonFocusAndSelectValueAfterReset = input(false, {
    // This is the key part! It coerces the attribute presence into a boolean 'true'.
    transform: booleanAttribute,
  });

  /**
   * @input formControlOverride
   * @description
   * Optional input to explicitly provide the `FormControl` instance that the clear button should
   * interact with. This is particularly useful when the `mat-form-field` is nested within
   * structures like `mat-radio-group` where the directive might not automatically resolve the
   * correct `NgControl`. Using this input ensures the clear button always targets the intended form control.
   */
  public formControlOverride = input<FormControl | null>(null);

  /** Emit click event on rsClearFormFieldButton
   *
   * Can be used for triggering custom callback after the default one
   *
   * Usage:
   *
   * ```html
   <mat-form-field (onRsClearFormFieldButtonClicked)='Function() > Function to execute after click event'>
   ...
   </mat-form-field>
   ```
   */
  @Output('onRsClearFormFieldButtonClicked') public onRsClearFormFieldButtonClicked: EventEmitter<MouseEvent> =
    new EventEmitter();
  private closeButton!: HTMLElement;
  private inputField!: AbstractControl;
  private buttonWrapper!: HTMLElement;
  private clearButtonLandingTag!: HTMLElement;
  private closeIcon!: HTMLElement;
  private formControlName: string | undefined;
  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    @Optional() public ngControl: NgControl,
    @Optional() private matFormField: MatFormField,
    private readonly hostElement: ElementRef,
    private readonly renderer: Renderer2,
    private readonly viewContainerRef: ViewContainerRef,
  ) {}

  private _buttonDisabled: boolean = false;

  /** disable or not clear button
   *
   * Usage:
   *
   * ```html
   <mat-form-field [rsClearFormFieldButtonDisabled]='boolean value'>
   ...
   </mat-form-field>
   ```
   **/
  @Input('rsClearFormFieldButtonDisabled')
  public set buttonDisabled(isDisabled: boolean) {
    this._buttonDisabled = isDisabled;
    this.shouldDisplayButton();
  }

  public ngOnInit(): void {
    this.createAndRenderCloseButton();
  }

  public ngAfterViewInit(): void {
    this.addListenersToButton();
    this.appendCloseButtonToParent();
    // Add mat-mdc-form-field-has-icon-suffix class to respect material rules
    this.hostElement.nativeElement.classList.add('mat-mdc-form-field-has-icon-suffix');

    //Set the tabindex attribute to -1 in the afterViewInit to ensure it's added properly
    this.renderer.setAttribute(this.closeButton, 'tabindex', '-1');
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private createAndRenderCloseButton(): void {
    this.closeButton = this.viewContainerRef.createComponent(MatIconButton).instance._elementRef.nativeElement;
    this.closeIcon = this.viewContainerRef.createComponent(MatIcon).instance._elementRef.nativeElement;

    this.clearButtonLandingTag = this.hostElement.nativeElement.querySelector(
      '.mat-mdc-form-field-flex',
    ) as HTMLDivElement;

    this.buttonWrapper = this.renderer.createElement('div');
    this.renderer.setAttribute(this.closeButton, 'type', 'button');

    this.renderer.addClass(this.closeButton, 'rs-clear-form-field-button');
    this.renderer.addClass(this.buttonWrapper, 'mat-mdc-form-field-icon-suffix');
    this.renderer.addClass(this.buttonWrapper, 'rs-clear-form-field-button--wrapper');
  }

  private addListenersToButton(): void {
    const ngControl = this.ngControl ? this.ngControl : this.matFormField._control.ngControl; // See explanation (*1) below

    this.inputField = this.formControlOverride()
      ? this.formControlOverride()!
      : (ngControl?.control as AbstractControl);

    this.formControlName = (ngControl as NgControl)?.name?.toString();

    this.renderer.addClass(this.closeButton, 'rs-clear-form-field-button-' + this.formControlName);

    this.inputField.valueChanges.pipe(delay(0), takeUntil(this.destroy$)).subscribe(() => {
      this.shouldDisplayButton();
    });

    this.shouldDisplayButton(); // In case of default value
    this.closeButton.addEventListener('click', this.onClick.bind(this));
  }

  private appendCloseButtonToParent(): void {
    this.renderer.appendChild(this.closeIcon, this.renderer.createText('close'));

    const iconWrapper = this.renderer.createElement('div');

    this.renderer.addClass(iconWrapper, 'rs-clear-form-field-button-icon-wrapper');
    this.renderer.appendChild(iconWrapper, this.closeIcon);
    this.renderer.appendChild(this.closeButton, iconWrapper);
    this.renderer.appendChild(this.buttonWrapper, this.closeButton);
    this.renderer.appendChild(this.clearButtonLandingTag, this.buttonWrapper);
  }

  private shouldDisplayButton(): void {
    if (!this.closeButton) {
      return;
    }

    const formFieldHasValue = this.ngControl?.valueAccessor
        ? Boolean(this.matFormField._control.value)
        : this.inputField?.rsHasValue(),
      isReadOnly = this.matFormField._elementRef.nativeElement.classList.contains('rs-datepicker')
        ? false
        : (this.matFormField._control as { _readonly?: boolean })['_readonly'];

    this.renderer[formFieldHasValue && !this._buttonDisabled && !isReadOnly ? 'removeClass' : 'addClass'](
      this.buttonWrapper,
      'rs-clear-form-field-button--disabled',
    );
  }

  private onClick(event: MouseEvent): void {
    const resetValue = this.rsClearFormFieldButtonResetValue();
    // We use !== undefined to detect if the input was explicitly provided (even if null or 0)
    const shouldSelectContent = resetValue !== undefined;
    const nativeInput = this.hostElement.nativeElement.querySelector('input, textarea') as HTMLInputElement;

    if (this.rsClearFormFieldButtonCallback) {
      this.rsClearFormFieldButtonCallback();
    } else if (resetValue !== undefined) {
      this.inputField.reset(resetValue);
    } else {
      this.inputField.reset();
    }

    if (this.rsClearFormFieldButtonFocusAndSelectValueAfterReset() || shouldSelectContent) {
      if (nativeInput) {
        nativeInput.focus();
        // Use requestAnimationFrame to defer the action until the DOM has rendered the reseted value
        requestAnimationFrame(() => {
          nativeInput.select();
        });
      }
    }

    this.onRsClearFormFieldButtonClicked.emit(event);

    this.inputField.markAsTouched();
    this.inputField.markAsDirty();
    event.stopPropagation();
  }
}

/** (*1)
 * We first check the ngControl because if it is an external form field implementing the ControlValueAccessor.
 * In this specific case the formControl that we are looking for is passed to the matFormField via the external component,
 * and formControl inside the matFormField is irrelevant as we target the formControl of the form and not the one of the custom field.
 * ex: here we see that the formControlName is passed via the external component
 <ui-renta-activity-task-select
 [required]="true"
 formControlName="activityTask"
 [disabled]="!companyThresholdForm.get('activityDefinition')?.valid"
 >
 </ui-renta-activity-task-select>
 * */
